import {
	Dispatch,
	FC,
	PropsWithChildren,
	SetStateAction,
	createContext,
	useEffect,
	useMemo,
} from 'react';

import { ComboboxItem } from '@mantine/core';
import { useLocalStorage, useSessionStorage } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { setPerson, setReplacedPersonId } from '@/store/user-process/user-process';
import {
	selectPerson,
	selectReplacedPersonId,
	selectUser,
} from '@/store/user-process/user-selectors';

import { useLazyGetPersonByUserIdQuery } from '@/api/persons';

import { ratingApi } from '@/shared/api/rating';
import { isAdmin, isModerator } from '@/shared/helpers/isAdmin';
import { useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { AutorizationResponseType, ILocation, IRate, TDateRange } from '@/shared/types/app.types';
import { IObject } from '@/shared/types/bookingObjects.types';
import { IPersonPass } from '@/shared/types/passes.types';
import { IUser } from '@/shared/types/user.types';

import { isReportsPermission } from '@/utils/isReportsPermission.ts';

import { useAppDispatch, useAppSelector } from '@/hooks';

type AppContextType = {
	user: AutorizationResponseType;
	passes: IPersonPass[];
	date: TDateRange;
	userRate: IRate;
	location: ILocation;
	currentLocation: IObject;
	carPasses: Array<ComboboxItem>;
	admin: boolean;
	moderator: boolean;
	isReportsAvailable: boolean;
	setUserRate: Dispatch<SetStateAction<IRate>>;
	setDate: Dispatch<SetStateAction<TDateRange>>;
	setCurrentLocation: Dispatch<SetStateAction<AppContextType['currentLocation']>>;
	setLocation: Dispatch<SetStateAction<AppContextType['location']>>;
};

export const AppContext = createContext<AppContextType>({} as AppContextType);

export const AppContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [location, setLocation] = useLocalStorage<AppContextType['location']>({
		key: 'location',
		defaultValue: {} as ILocation,
	});
	const [user] = useLocalStorage<AppContextType['user']>({ key: 'user' });
	const passes = useUserStore((state) => state.passes);
	const [date, setDate] = useSessionStorage<AppContextType['date']>({ key: 'date' });
	const [userRate, setUserRate] = useSessionStorage<AppContextType['userRate']>({ key: 'rate' });
	const defaultCarNumber = useSettingsStore((state) => state.carNumber);
	const setDefaultCarNumber = useSettingsStore((state) => state.setCarNumber);
	const setCarPasses = useUserStore((state) => state.setCarPasses);
	const replacedPersonId = useAppSelector(selectReplacedPersonId);
	const sessionPersonId = useAppSelector(selectUser)?.user.person_id;
	const person = useAppSelector(selectPerson);
	const setZustandUser = useUserStore((state) => state.setUser);
	const [loadPerson] = useLazyGetPersonByUserIdQuery();

	const dispatch = useAppDispatch();

	const [currentLocation, setCurrentLocation] = useLocalStorage<AppContextType['currentLocation']>({
		key: 'currentLocation',
	});

	const carPasses = useMemo(
		() =>
			passes
				?.filter((pass) => pass?.pass_type?.kind === 'car' && !pass.is_deleted)
				?.map(({ id, car_number }) => ({ value: String(id), label: car_number })),
		[passes],
	);

	useEffect(() => {
		setCarPasses(carPasses);

		// Пустой список бывает и до загрузки пропусков — номер по умолчанию в этом случае не сбрасываем
		if (carPasses?.length && !carPasses.some((pass) => pass.value === defaultCarNumber?.value)) {
			setDefaultCarNumber(carPasses[0]);
		}
	}, [carPasses, defaultCarNumber]);
	const admin = isAdmin(user);
	const isReportsAvailable = isReportsPermission(user);
	const moderator = isModerator(passes);

	const { data: getPersonRating } = useQuery({
		queryKey: ['rating', user],
		queryFn: () =>
			user
				? ratingApi.getPersonRating(user?.user.person_id).then((res) => {
						const { rating, max_default_days, max_parking_days } = res.data;

						setUserRate({
							maxDefaultDays: max_default_days,
							maxParkingDays: max_parking_days,
							rating,
						});
						return res.data;
					})
				: {},
	});

	useEffect(() => {
		if (user?.user.person_id && !replacedPersonId) {
			dispatch(setReplacedPersonId(user.user.person_id));
		}
	}, [user]);

	// Профиль не сохраняется, если вход или продление сессии прервались, — восстанавливаем его
	useEffect(() => {
		if (!sessionPersonId || person) {
			return;
		}

		void loadPerson(sessionPersonId)
			.unwrap()
			.then((loadedPerson) => {
				dispatch(setPerson(loadedPerson));
				setZustandUser(loadedPerson as unknown as IUser);

				// Без сохранённой сессии («Запомнить меня» выключено) профиль тоже не сохраняем
				if (localStorage.getItem('user')) {
					localStorage.setItem('person', JSON.stringify(loadedPerson));
				}
			})
			.catch(() => undefined);
	}, [sessionPersonId, person]);

	return (
		<AppContext.Provider
			value={{
				location,
				date,
				userRate,
				user,
				passes,
				currentLocation,
				carPasses,
				admin,
				moderator,
				isReportsAvailable,
				setUserRate,
				setDate,
				setCurrentLocation,
				setLocation,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};
